import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Typography, useTheme } from "@mui/material";
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { TrashIcon } from "@heroicons/react/24/outline";
import { CiSearch } from "react-icons/ci";
import api from "./components/login/interceptors";
import usePlanGate from "./hooks/usePlanGate";
import { PaginationButtons, RowsPerPageSelector } from "./utils/paginationUtils";
import AddInterviewerModal from "./components/modals/AddInterviewerModal";
import useFocusTrap from "./hooks/useFocusTrap";
import { fetchPanelistPage, formatPanelistExperience, panelistDisplayName, panelistErrorMessage, panelistIdOf, panelistsUrl } from "./utils/panelists";
import SkillChipsPopover from "./components/table/SkillChipPopover";

const InterviewersPage = () => {
  const theme = useTheme();
  // Adding a panelist uses a panelist seat
  const panelistGate = usePlanGate("panelist_seats");
  const firstHeaderCellRef = useRef(null);
  const [interviewers, setInterviewers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingInterviewer, setEditingInterviewer] = useState(null);
  const [personToDelete, setPersonToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const isDeletingRef = useRef(false);
  isDeletingRef.current = isDeleting;
  const deleteModalRef = useFocusTrap(Boolean(personToDelete), () => {
    if (isDeletingRef.current) return;
    setPersonToDelete(null);
    setDeleteError("");
  });
  const skipSearchReset = useRef(true);

  const fetchInterviewers = async () => {
    setIsLoading(true);
    setListError("");
    try {
      const result = await fetchPanelistPage({
        search: appliedSearch,
        page: currentPage,
        limit: rowsPerPage,
      });
      setInterviewers(result.panelists);
      setTotalCount(result.count);
    } catch (err) {
      console.error("Error fetching panelists:", err);
      setInterviewers([]);
      setTotalCount(0);
      setListError(panelistErrorMessage(err, "Could not load panelists."));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (skipSearchReset.current) {
      skipSearchReset.current = false;
      return undefined;
    }
    const timer = setTimeout(() => {
      setAppliedSearch(searchQuery.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchInterviewers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedSearch, currentPage, rowsPerPage]);

  const sortedInterviewers = useMemo(() => {
    return [...interviewers].sort((a, b) => {
      if (sortField === "experience") {
        const aYears = a.experience === 0 || a.experience ? Number(a.experience) : -1;
        const bYears = b.experience === 0 || b.experience ? Number(b.experience) : -1;
        return sortOrder === "asc" ? aYears - bYears : bYears - aYears;
      }
      const aValue = (sortField === "name" ? panelistDisplayName(a) : (a[sortField] || "")).toString().toLowerCase();
      const bValue = (sortField === "name" ? panelistDisplayName(b) : (b[sortField] || "")).toString().toLowerCase();
      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [interviewers, sortField, sortOrder]);

  const totalPages = Math.ceil(totalCount / rowsPerPage) || 1;
  const rows = sortedInterviewers;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setPersonToDelete(null);
    setDeleteError("");
  };

  const handleDelete = async () => {
    if (!personToDelete) return;
    setIsDeleting(true);
    setDeleteError("");
    try {
      await api.delete(`${panelistsUrl}/${panelistIdOf(personToDelete)}`);
      setPersonToDelete(null);
      fetchInterviewers();
    } catch (err) {
      console.error("Error deleting panelist:", err);
      setDeleteError(panelistErrorMessage(err, "Could not delete panelist."));
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setTimeout(() => {
      firstHeaderCellRef.current?.focus();
    }, 0);
  };

  const headerCellSx = {
    backgroundColor: theme.palette.secondary.v5,
    color: theme.palette.text.main,
    zIndex: 10,
  };

  const roleColumnSx = {
    width: 220,
    maxWidth: 220,
  };

  return (
    <div
      className="flex flex-col w-full h-full"
      style={{ backgroundColor: theme.palette.primary.v2, color: theme.palette.text.main }}
    >
      <div
        className="w-full flex flex-wrap justify-between items-center gap-2 py-5 px-4 sm:px-6 max-sm:pl-12 border border-l-0 border-r-0 shadow-md"
        style={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.text.main,
          borderColor: theme.palette.secondary.v4,
        }}
      >
        <h1 className="font-semibold text-2xl">Panelists</h1>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-80">
            <CiSearch
              style={{
                position: "absolute",
                left: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                color: theme.palette.text.main,
                pointerEvents: "none",
                fontSize: "16px",
              }}
            />
            <input
              type="text"
              placeholder="Search name or email"
              aria-label="Search name or email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`border px-2 py-1 pl-8 text-sm rounded w-full focus:outline-none focus:ring-0 focus:border ${searchQuery ? "pr-8" : ""}`}
              style={{
                borderColor: theme.palette.secondary.main,
                color: theme.palette.text.main,
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-accent-gray-medium hover:text-gray-700"
                aria-label="Clear search"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            {...panelistGate.controlProps}
            onClick={panelistGate.guard(() => {
              setEditingInterviewer(null);
              setIsModalOpen(true);
            })}
            sx={{
              backgroundColor: theme.palette.info.main,
              color: theme.palette.text.light,
              textTransform: "none",
              minWidth: "max-content",
            }}
          >
            Add Panelist
          </Button>
          {panelistGate.hint}
        </div>
      </div>

      {isLoading ? (
        <div className="mx-auto px-6 text-center mt-16">
          <div className="spinner-border animate-spin inline-block w-8 h-8 border-2 border-t-4 border-black rounded-full" />
          <p>Loading panelists...</p>
        </div>
      ) : (
        <>
          <div
            // The frame belongs to the table; an error with no rows to frame stands on its own
            className={`overflow-auto m-6 max-md:mx-4 mb-0 ${listError && sortedInterviewers.length === 0 ? "" : "border rounded-md"}`}
            style={{ color: theme.palette.text.main, borderColor: theme.palette.secondary.v4 }}
          >
            {listError && (
              <div className={`p-3 bg-red-100 border border-red-400 text-red-700 rounded ${sortedInterviewers.length === 0 ? "" : "m-4"}`}>{listError}</div>
            )}
            {sortedInterviewers.length === 0 ? (
              listError ? null : (
              <div
                className="flex flex-col items-center justify-center h-80 text-accent-gray"
                style={{ background: theme.palette.primary.main }}
              >
                <p className="text-lg font-medium mb-1">No panelists yet</p>
                <p className="text-sm max-w-md text-center px-6">
                  Panelists you add will appear here.
                </p>
              </div>
              )
            ) : (
              <Table stickyHeader className="w-full min-w-max" sx={{ backgroundColor: theme.palette.primary.main }}>
                <TableHead>
                  <TableRow>
                    <TableCell ref={firstHeaderCellRef} tabIndex={0} sx={{ ...headerCellSx, paddingLeft: 2 }}>
                      <TableSortLabel
                        active={sortField === "name"}
                        direction={sortField === "name" ? sortOrder : "asc"}
                        onClick={() => handleSort("name")}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ ...headerCellSx, paddingLeft: 2 }}>
                      <TableSortLabel
                        active={sortField === "email"}
                        direction={sortField === "email" ? sortOrder : "asc"}
                        onClick={() => handleSort("email")}
                      >
                        Email
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ ...headerCellSx, ...roleColumnSx, paddingLeft: 2 }}>
                      <TableSortLabel
                        active={sortField === "role"}
                        direction={sortField === "role" ? sortOrder : "asc"}
                        onClick={() => handleSort("role")}
                      >
                        Designation
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ ...headerCellSx, paddingLeft: 2 }}>
                      <TableSortLabel
                        active={sortField === "experience"}
                        direction={sortField === "experience" ? sortOrder : "asc"}
                        onClick={() => handleSort("experience")}
                      >
                        Experience
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ ...headerCellSx, paddingLeft: 2 }}>Skills</TableCell>
                    <TableCell sx={{ ...headerCellSx, paddingLeft: 2 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((person) => (
                    <TableRow key={panelistIdOf(person) || person.email}>
                      <TableCell sx={{ width: 'max-content', maxWidth: 220, whiteSpace: "normal", overflowWrap: "anywhere", wordBreak: "break-word", paddingLeft: 2, }}>{panelistDisplayName(person) || "—"}</TableCell>
                      <TableCell>
                        <Typography sx={{ wordBreak: "break-all", paddingLeft: 2 }}>{person.email || "—"}</Typography>
                      </TableCell>
                      <TableCell sx={roleColumnSx}>
                        <Typography
                          component="div"
                          sx={{
                            width: 'max-content',
                            maxWidth: 220,
                            whiteSpace: "normal",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                            paddingLeft: 2,
                          }}
                        >
                          {person.role || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ paddingLeft: 2 }}>{formatPanelistExperience(person)}</TableCell>
                      <SkillChipsPopover
                        wrapSingle
                        maxWidth={220}
                        cellSx={{ pl: 2, pr: 0, width: 'max-content', }}
                        skills={Array.isArray(person.skills) ? [...person.skills] : []}
                      />
                      <TableCell sx={{ paddingLeft: 2 }}>
                        <Button
                          onClick={() => {
                            setEditingInterviewer(person);
                            setIsModalOpen(true);
                          }}
                          sx={{
                            color: theme.palette.info.main,
                            textTransform: "none",
                            minWidth: "max-content",
                          }}
                          style={{ padding: "0", backgroundColor: "transparent" }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon" class="h-3 w-4"><path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125"></path></svg>
                        </Button>
                        <Button
                          onClick={() => {
                            setDeleteError("");
                            setPersonToDelete(person);
                          }}
                          aria-label="Delete"
                          sx={{
                            color: theme.palette.error?.main || "#b91c1c",
                            textTransform: "none",
                            minWidth: "max-content",
                          }}
                        >
                          <TrashIcon className="h-3 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          {sortedInterviewers.length > 0 && (
            <div className="mt-auto w-full flex flex-col justify-center items-center px-3 py-3 sm:px-6">
              <div className="relative flex max-lg:flex-col w-full justify-center items-center">
                <RowsPerPageSelector
                  rowsPerPage={rowsPerPage}
                  handleChangeRowsPerPage={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setCurrentPage(1);
                  }}
                />
                <nav aria-label="Pagination" className="flex flex-wrap justify-center items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage > 1 ? currentPage - 1 : 1)}
                    className="relative inline-flex items-center border rounded px-2 py-2 bg-white hover:bg-accent-blue hover:text-white text-xs sm:text-sm"
                    style={{ borderColor: theme.palette.secondary.v4 }}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeftIcon aria-hidden="true" className="h-5 w-5" />
                    <span className="hidden xs:inline ml-1">Prev</span>
                  </button>
                  <PaginationButtons
                    currentPage={currentPage}
                    totalPages={totalPages}
                    handlePageChange={handlePageChange}
                    theme={theme}
                  />
                  <button
                    onClick={() => handlePageChange(currentPage < totalPages ? currentPage + 1 : totalPages)}
                    className="relative inline-flex items-center rounded px-2 py-2 bg-white hover:bg-accent-blue hover:text-white text-xs sm:text-sm border"
                    style={{ borderColor: theme.palette.secondary.v4 }}
                    disabled={currentPage === totalPages}
                  >
                    <span className="hidden xs:inline mr-1">Next</span>
                    <ChevronRightIcon aria-hidden="true" className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          )}
        </>
      )}
      {personToDelete && (
        <div className="fixed inset-0 flex items-start justify-center z-50 bg-black bg-opacity-70">
          <div
            ref={deleteModalRef}
            className="modal-content relative w-full max-w-md bg-white rounded-lg shadow-xl flex flex-col overflow-hidden max-sm:mx-2 my-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-panelist-title"
          >
            <div
              className="flex justify-between items-center py-4 px-5 border-b"
              style={{
                backgroundColor: theme.palette.primary.main,
                borderColor: theme.palette.secondary.v4,
              }}
            >
              <h2 id="delete-panelist-title" className="font-semibold">
                Delete Panelist
              </h2>
              <button
                onClick={closeDeleteModal}
                aria-label="Close modal"
                className="inline-flex items-center justify-center rounded-full bg-black text-white h-6 w-6 hover:bg-gray-800 transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="px-5 sm:px-6 py-6">
              <p style={{ color: theme.palette.text.main }}>
                Are you sure you want to delete
                <span className="px-1 font-bold">
                  {panelistDisplayName(personToDelete) || "this panelist"}
                </span>
                ?
              </p>
              {deleteError && (
                <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {deleteError}
                </div>
              )}
            </div>
            <div
              className="px-5 py-3 border-t flex justify-end gap-3"
              style={{ borderColor: theme.palette.secondary.v4 }}
            >
              <Button
                onClick={closeDeleteModal}
                disabled={isDeleting}
                sx={{
                  color: theme.palette.text.main,
                  textTransform: "none",
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={isDeleting}
                sx={{
                  backgroundColor: theme.palette.error?.main || "#b91c1c",
                  color: theme.palette.text.light,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: theme.palette.error?.dark || "#991b1b",
                  },
                }}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
      <AddInterviewerModal
        isOpen={isModalOpen}
        interviewer={editingInterviewer}
        onClose={() => {
          setIsModalOpen(false);
          setEditingInterviewer(null);
        }}
        onCreated={fetchInterviewers}
      />
    </div>
  );
};

export default InterviewersPage;
